import CreditCardRoundedIcon from '@mui/icons-material/CreditCardRounded';
import ReceiptRoundedIcon from '@mui/icons-material/ReceiptRounded';
import { Stack } from '@mui/material';

import {
  createPaymentProvider,
  deletePaymentProvider,
  listPaymentProviders,
  testPaymentProviderConnection,
  updatePaymentProvider,
} from '../../api/paymentSettingsApi';
import { settingsKeys, usePaymentSettings, useUpdatePaymentSettings } from '../../hooks';
import { toSettingsUpdate, useDomainPermissions, useFeedback, useSettingsForm } from '../../lib';
import type { PaymentCaptureMode, PaymentMethod } from '../../model/settingsTypes';
import {
  ChipListField,
  FeedbackSnackbar,
  MultiToggleField,
  ProviderManager,
  SelectField,
  SettingsCard,
  SettingsScaffold,
  SwitchField,
  TextInputField,
} from '../components';
import { PAYMENT_CAPTURE_MODE_OPTIONS, PAYMENT_METHOD_OPTIONS } from '../options';

/** Payment methods, providers, fiscalisation and credentials. */
export function PaymentSettingsPage() {
  const { canView, canManage, canManageCredentials } = useDomainPermissions('payments');
  const query = usePaymentSettings();
  const update = useUpdatePaymentSettings();
  const form = useSettingsForm(query.data);
  const { feedback, notify, success, error: notifyError, clear } = useFeedback();

  const handleSave = () => {
    if (!form.draft) {
      return;
    }
    update.mutate(toSettingsUpdate(form.draft), {
      onSuccess: () => success('Payment settings saved.'),
      onError: (error) => {
        notifyError(error.message);
        if (error.status === 409) {
          void query.refetch();
        }
      },
    });
  };

  return (
    <SettingsScaffold
      title="Payment settings"
      subtitle="Payment methods, capture mode, providers and fiscalisation."
      icon={<CreditCardRoundedIcon />}
      query={query}
      canView={canView}
      canManage={canManage}
      audit={query.data}
      dirty={form.dirty}
      saving={update.isPending}
      onSave={handleSave}
      onReset={form.reset}
      feedback={<FeedbackSnackbar feedback={feedback} onClose={clear} />}
    >
      {() => {
        const draft = form.draft;
        if (!draft) {
          return null;
        }
        const disabled = !canManage;
        return (
          <Stack spacing={2.5}>
            <SettingsCard title="Methods" icon={<CreditCardRoundedIcon />}>
              <MultiToggleField
                label="Enabled methods"
                value={draft.enabledMethods}
                options={PAYMENT_METHOD_OPTIONS}
                onChange={(value) => form.setField('enabledMethods', value as PaymentMethod[])}
                disabled={disabled}
              />
              <SelectField
                label="Capture mode"
                value={draft.captureMode}
                options={PAYMENT_CAPTURE_MODE_OPTIONS}
                onChange={(value) => form.setField('captureMode', value as PaymentCaptureMode)}
                disabled={disabled}
              />
              <SwitchField
                label="Refunds"
                description="Allow refunds to be issued from the back office."
                checked={draft.refundsEnabled}
                onChange={(value) => form.setField('refundsEnabled', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <SettingsCard title="Fiscalisation & rules" icon={<ReceiptRoundedIcon />}>
              <TextInputField
                label="Fiscal provider"
                description="Fiscal receipt provider code."
                value={draft.fiscalProvider ?? ''}
                onChange={(value) => form.setField('fiscalProvider', value || null)}
                disabled={disabled}
              />
              <ChipListField
                label="Restrictions"
                value={draft.restrictions}
                onChange={(value) => form.setField('restrictions', value)}
                disabled={disabled}
              />
            </SettingsCard>

            <ProviderManager
              title="Payment providers"
              description="Acquirers / PSPs and their credentials."
              icon={<CreditCardRoundedIcon />}
              listKey={settingsKeys.paymentProviders}
              api={{
                list: listPaymentProviders,
                create: createPaymentProvider,
                update: updatePaymentProvider,
                remove: deletePaymentProvider,
                test: testPaymentProviderConnection,
              }}
              canManage={canManage}
              canManageCredentials={canManageCredentials}
              credentialHint="PSP secret key"
              onFeedback={notify}
            />
          </Stack>
        );
      }}
    </SettingsScaffold>
  );
}
